// src/pages/ebilling/OtpVerificationPage.js
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
    Container,
    Box,
    Button,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    Stack,
    TextField
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiEbilling } from '../../api/ebilling';

const CONSENT_ID = process.env.REACT_APP_E_CONSENT_CONSENT_ID;
const OTP_EMAIL_GUIDE_URL = process.env.REACT_APP_E_CONSENT_OTP_EMAIL_GUIDE_URL;

const OtpVerificationPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [otpValues, setOtpValues] = useState(['', '', '', '', '', '']);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');
    const [customerId, setCustomerId] = useState('');
    const [email, setEmail] = useState('');
    const [otpRef, setOtpRef] = useState('');
    const [otpId, setOtpId] = useState('');
    const [expireTime, setExpireTime] = useState('');
    const [createEpoch, setCreateEpoch] = useState('');
    const [owner, setOwner] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [timeLeft, setTimeLeft] = useState(0);
    const [canResend, setCanResend] = useState(false);
    const [showPointDetailFromUrl, setShowPointDetailFromUrl] = useState("");
    const otpRefs = useRef([]);

    useEffect(() => {
        const state = location.state;
        const params = new URLSearchParams(location.search);
        const customerIdFromUrl = params.get('customerId');
        const showPointDetailFromUrl = params.get("showPointDetail");
        setShowPointDetailFromUrl(showPointDetailFromUrl);

        if (state) {
            setEmail(state.email || '');
            setCustomerId(state.customerId || customerIdFromUrl || '');
            setOtpRef(state.otpRef || '');
            setOtpId(state.otpId || '');
            setExpireTime(state.expireTime || '');
            setCreateEpoch(state.createEpoch || '');
            setOwner(state.owner || '');
            setPhoneNumber(state.phoneNumber || '');
            setFirstName(state.firstName || '');
            setLastName(state.lastName || '');

            if (state.expireTime) {
                const expireTime = new Date(state.expireTime);
                const currentTime = new Date();

                if (isNaN(expireTime.getTime())) {
                    setTimeLeft(141);
                    return;
                }

                const timeDiff = Math.floor((expireTime - currentTime) / 1000);
                if (timeDiff > 0) {
                    setTimeLeft(timeDiff);
                } else {
                    setTimeLeft(0);
                    setCanResend(true);
                }
            } else {
                setTimeLeft(141);
            }
        } else {
            if (customerIdFromUrl) {
                setCustomerId(customerIdFromUrl);
                setTimeLeft(141);
            } else {
                setMessage('ไม่พบข้อมูล Customer ID กรุณาเข้าสู่ระบบใหม่');
                setMessageType('error');
            }
        }
    }, [location.state, location.search]);

    useEffect(() => {
        if (timeLeft > 0) {
            const timer = setTimeout(() => {
                setTimeLeft(timeLeft - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else {
            const currentTime = new Date();
            const expireTimeDate = new Date(expireTime);
            if (currentTime > expireTimeDate) {
                setCanResend(true);
            }
        }
    }, [timeLeft, expireTime]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleOtpChange = (index, value) => {
        // Only allow numbers
        if (!/^\d*$/.test(value)) return;

        if (value.length > 1) return;

        const newOtpValues = [...otpValues];
        newOtpValues[index] = value;
        setOtpValues(newOtpValues);

        if (value && index < 5) {
            otpRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
            otpRefs.current[index - 1]?.focus();
        }
    };

    const verifyOtpMutation = useMutation({
        mutationFn: async (payload) => {
            return await apiEbilling.checkOTP(payload);
        },
        onSuccess: (response) => {
            if (response.success) {
                setMessage('ยืนยัน OTP สำเร็จ');
                setMessageType('success');

                setTimeout(() => {
                    navigate(`/consent?customerId=${customerId}&consentId=${CONSENT_ID}&showPointDetail=${showPointDetailFromUrl}`, {
                        state: {
                            email: email,
                            customerId: customerId,
                            consentId: CONSENT_ID,
                            phoneNumber: phoneNumber,
                            otpRef: otpRef,
                            firstName: firstName,
                            lastName: lastName,
                        }
                    });
                }, 1500);
            } else {
                setMessage('รหัส OTP ไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง');
                setMessageType('error');
                setOtpValues(['', '', '', '', '', '']);
                otpRefs.current[0]?.focus();
            }
        },
        onError: (error) => {
            if (error?.errorCode === '401') {
                setMessage('รหัส OTP ไม่ถูกต้อง กรุณาลองใหม่อีกครั้ง');
                setMessageType('error');
                setOtpValues(['', '', '', '', '', '']);
                otpRefs.current[0]?.focus();
            } else {
                navigate(`/close?customerId=${customerId}&status=false&msgErr=${encodeURIComponent(error.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล')}`);
            }
        },
    });

    const resendOtpMutation = useMutation({
        mutationFn: async (payload) => {
            return await apiEbilling.createVerifyOTPEmail(payload);
        },
        onSuccess: (response) => {
            if (response.success && response.data) {
                setMessage('ส่งรหัส OTP ใหม่เรียบร้อยแล้ว');
                setMessageType('success');

                if (response.data.data) {
                    setOtpRef(response.data.data.otp_ref || '');
                    setOtpId(response.data.data.id || '');
                    setExpireTime(response.data.data.expire_time || '');
                    setCreateEpoch(response.data.data.create_epoch || '');
                    setOwner(response.data.data.owner || '');

                    if (response.data.data.expire_time) {
                        const expireTime = new Date(response.data.data.expire_time);
                        const currentTime = new Date();
                        const timeDiff = Math.floor((expireTime - currentTime) / 1000);
                        setTimeLeft(timeDiff > 0 ? timeDiff : 141);
                    } else {
                        setTimeLeft(141);
                    }
                }

                setCanResend(false);
                setOtpValues(['', '', '', '', '', '']);
                otpRefs.current[0]?.focus();
            } else {
                setMessage('เกิดข้อผิดพลาดในการส่งรหัส OTP ใหม่');
                setMessageType('error');
            }
        },
        onError: (error) => {
            navigate(`/close?customerId=${customerId}&status=false&msgErr=${encodeURIComponent(error.message || 'เกิดข้อผิดพลาดในการส่ง OTP ใหม่')}`);
        },
    });

    const handleSubmit = (e) => {
        e.preventDefault();

        const otpCode = otpValues.join('');

        if (otpCode.length !== 6) {
            setMessage('กรุณากรอกรหัส OTP ให้ครบ 6 หลัก');
            setMessageType('error');
            return;
        }

        setMessage('');
        setMessageType('info');

        const payload = {
            id: otpId,
            phone_number: "",
            email: email,
            otp: otpCode,
            otp_ref: otpRef,
            expire_time: expireTime,
            create_epoch: createEpoch,
            owner: owner,
            customer_id: customerId
        };

        verifyOtpMutation.mutate(payload);
    };

    const handleResendOtp = () => {
        const currentTime = new Date();
        const expireTimeDate = new Date(expireTime);

        if (!canResend || currentTime < expireTimeDate) return;

        resendOtpMutation.mutate({
            email: email,
            owner: 'ebilling-consent',
            customer_id: customerId,
            firstName: firstName,
            lastName: lastName,
        });
    };

    const handleGoBack = () => {
        navigate(`/?customerId=${customerId}`);
    };

    return (
        <Container
            component="main"
            maxWidth={false}
            disableGutters
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', sm: 'center' },
                minHeight: '100vh',
                p: { xs: 0, sm: 2 },
                bgcolor: { xs: 'white', sm: '#f5f7fa' }, // Professional background for browser
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    borderRadius: { xs: 0, sm: 3 },
                    width: '100%',
                    maxWidth: { xs: 'none', sm: 450 }, // 9:16 Aspect Ratio approximation
                    minHeight: { xs: '100vh', sm: 800 }, // 9:16 Aspect Ratio approximation
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch', // Always stretch to fill card width
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    boxShadow: { xs: 'none', sm: '0 8px 24px rgba(0,0,0,0.05)' } // Shadow for browser card
                }}
            >
                {/* Logo and Header Section */}
                <Box
                    sx={{
                        width: '100%',
                        bgcolor: 'white',
                        p: { xs: 3, sm: 6 }, // Balanced vertical space in header (48px)
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        component="img"
                        src="/image/logo-header.png"
                        alt="SG CAPITAL"
                        sx={{
                            height: 60,
                            mb: 2,
                            objectFit: 'contain'
                        }}
                    />
                    <Typography
                        variant="h5"
                        component="h1"
                        sx={{
                            fontWeight: 500,
                            color: '#666666',
                            fontSize: '1.5rem'
                        }}
                    >
                        OTP Verification
                    </Typography>
                </Box>

                {/* Form Content - Takes remaining space */}
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        px: { xs: 2.5, sm: 6 }, // Increased horizontal padding for browser
                        pb: { xs: 5, sm: 6 }, // Consistent bottom padding
                        width: '100%',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {/* Content Area - Grows to fill space */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', sm: 'center' } }}>
                        <Stack spacing={4}>
                            {/* Title */}
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 600,
                                    color: '#333',
                                    fontSize: '1.3rem',
                                    textAlign: 'center'
                                }}
                            >
                                กรุณากรอกรหัส OTP ที่ได้รับทางอีเมล
                            </Typography>

                            {/* Email display */}
                            <Typography
                                variant="body1"
                                sx={{
                                    color: '#666',
                                    fontSize: '1rem',
                                    textAlign: 'center',
                                    lineHeight: 1.6
                                }}
                            >
                                ถูกจัดส่งผ่าน E-mail ไป
                                <br />
                                <Box component="span" sx={{ fontWeight: 600, color: '#333' }}>
                                    {email}
                                </Box>
                            </Typography>

                            {/* OTP Input Fields */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: { xs: 1, sm: 1.5 },
                                    justifyContent: 'center',
                                    // Removed my: 3 to prevent pushing against bottom text
                                }}
                            >
                                {otpValues.map((value, index) => (
                                    <TextField
                                        key={index}
                                        inputRef={(el) => (otpRefs.current[index] = el)}
                                        value={value}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        type="tel"
                                        slotProps={{
                                            input: {
                                                maxLength: 1,
                                                pattern: '[0-9]*',
                                                inputMode: 'numeric',
                                                style: {
                                                    textAlign: 'center',
                                                    fontSize: '1.5rem',
                                                    fontWeight: 'bold',
                                                    padding: 0 // Let root height handle centering
                                                }
                                            }
                                        }}
                                        sx={{
                                            width: { xs: 44, sm: 54 }, // Responsive width
                                            '& .MuiOutlinedInput-root': {
                                                height: { xs: 56, sm: 64 }, // Standardized height
                                                borderRadius: 2,
                                                backgroundColor: '#f8f9fa',
                                                '& fieldset': {
                                                    borderColor: '#e0e0e0',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: '#c0c0c0',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#1976d2',
                                                    borderWidth: 2
                                                },
                                                '& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
                                                    '-webkit-appearance': 'none',
                                                    margin: 0
                                                },
                                                '& input[type=number]': {
                                                    '-moz-appearance': 'textfield'
                                                },
                                                '& .MuiOutlinedInput-input': {
                                                    padding: 0,
                                                    textAlign: 'center',
                                                }
                                            }
                                        }}
                                    />
                                ))}
                            </Box>

                            {/* Reference number and timer */}
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                    variant="body2"
                                    sx={{ color: '#666', mb: 1 }}
                                >
                                    หมายเลขอ้างอิง : <Box component="span" sx={{ color: '#2196f3', fontWeight: 600 }}>{otpRef || 'XXXXXX'}</Box>
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: '#666' }}
                                >
                                    หมดเวลาใน : <Box component="span" sx={{ color: '#2196f3', fontWeight: 600 }}>{formatTime(timeLeft)}</Box>
                                </Typography>
                            </Box>

                            {/* Resend OTP Button */}
                            {timeLeft === 0 ? (
                                <Button
                                    onClick={handleResendOtp}
                                    disabled={resendOtpMutation.isPending || !canResend}
                                    sx={{
                                        color: '#2196f3',
                                        textDecoration: 'underline',
                                        fontSize: '0.9rem',
                                        textTransform: 'none',
                                        textAlign: 'center',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                            textDecoration: 'underline'
                                        },
                                        '&.Mui-disabled': {
                                            color: '#999'
                                        }
                                    }}
                                >
                                    {resendOtpMutation.isPending ? 'กำลังส่ง...' : 'ส่งรหัส OTP ใหม่'}
                                </Button>
                            ) : (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: '#999',
                                        fontSize: '0.9rem',
                                        textAlign: 'center'
                                    }}
                                >
                                    รอเวลาเพื่อส่ง OTP ใหม่
                                </Typography>
                            )}
                        </Stack>

                        {message && (
                            <Box sx={{ mt: 3, width: '100%' }}>
                                <Alert severity={messageType}>{message}</Alert>
                            </Box>
                        )}
                    </Box>
                    <Typography
                        variant="body2"
                        sx={{
                            color: '#000',
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            textAlign: 'center',
                            mt: 4,
                            mb: 2,
                            lineHeight: 1.5
                        }}
                    >
                        ไม่ได้รับอีเมล?{' '}
                        <Box
                            component="span"
                            onClick={() => window.open(OTP_EMAIL_GUIDE_URL, '_blank')}
                            sx={{
                                color: '#d32f2f',
                                fontSize: '0.95rem',
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'inline-block',
                                padding: '8px 12px',
                                margin: '-8px -12px',
                                verticalAlign: 'middle',
                                '&:hover': {
                                    color: '#b71c1c'
                                }
                            }}
                        >
                            คลิกที่นี่
                        </Box>
                    </Typography>

                    {/* Submit Button */}
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={verifyOtpMutation.isPending || otpValues.join('').length !== 6}
                        sx={{
                            borderRadius: 3,
                            fontSize: '1.2rem',
                            fontWeight: 500,
                            textTransform: 'none',
                            height: 64, // Fixed height to prevent jitter
                            backgroundColor: '#1976d2',
                            '&:hover': {
                                backgroundColor: '#1565c0',
                            },
                            '&:disabled': {
                                backgroundColor: '#e0e0e0',
                                color: '#a0a0a0'
                            }
                        }}
                    >
                        {verifyOtpMutation.isPending ? <CircularProgress size={24} color="inherit" /> : 'ยืนยัน OTP'}
                    </Button>

                    {/* Back Button at Bottom */}
                    <Button
                        onClick={handleGoBack}
                        fullWidth
                        variant="outlined"
                        disabled={resendOtpMutation.isPending || !canResend}
                        sx={{
                            mt: 2,
                            borderRadius: 3,
                            borderColor: '#e0e0e0',
                            color: '#666',
                            fontSize: '1.1rem',
                            fontWeight: 500,
                            textTransform: 'none',
                            height: 64, // Standardized height
                            '&:hover': {
                                borderColor: '#c0c0c0',
                                backgroundColor: '#f5f5f5',
                            },
                            '&:active': {
                                backgroundColor: '#eeeeee',
                            },
                            '&.Mui-disabled': {
                                borderColor: '#f0f0f0',
                                backgroundColor: '#fafafa',
                                color: '#bbb',
                            }
                        }}
                        startIcon={<ArrowBackIcon sx={{ fontSize: '1.5rem' }} />}
                    >
                        กลับหน้าแรก
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default OtpVerificationPage;